import styled from '@emotion/styled';
import { shouldForwardProp, container, responseFont } from '@landsbankinn/ui';
import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  Children,
  cloneElement,
} from 'react';
import { animated, useSpring } from 'react-spring';

import { LayoutContext } from '../../components/contexts/LayoutContext';
import { MobileSubnav } from '../../components/mobile-subnav/MobileSubnav';

interface SubnavProps {
  title: React.ReactNode;
  pageTitle?: string;
  children: React.ReactNode;
  active?: any;
  cta?: React.ReactNode;
  mobilecta?: React.ReactNode;
  parent?: React.ReactNode;
  mobileItems?: React.ReactNode;
  isTransparent?: boolean;
  titleWidth?: string;
  className?: string;
}

const Host = styled(animated.div, {
  shouldForwardProp: shouldForwardProp('div'),
})<{ isTransparent?: boolean }>`
  display: block;
  background: ${(props) => (props.isTransparent ? 'transparent' : 'white')};
  position: relative;
  left: 0;
  right: 0;
  z-index: 10;
  height: 72px;
  margin-bottom: -20px;
  @media (max-width: 949px) {
    display: none;
  }
`;

const Sticky = styled(animated.div, {
  shouldForwardProp: shouldForwardProp('div'),
})<{ isTransparent?: boolean }>`
  position: relative;
  /* position: fixed; */
  left: 0;
  right: 0;
  top: 0;
  background: ${(props) => (props.isTransparent ? 'transparent' : 'white')};
  z-index: 110;
`;

const Container = styled.nav`
  ${container}
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 72px;
`;

const ITEM_GUTTER = 14;

export const Item = styled.li<{ active?: boolean }>`
  padding-left: ${ITEM_GUTTER}px;
  padding-right: ${ITEM_GUTTER}px;
  padding-bottom: 6px;
  margin-bottom: -8px;
  transition: opacity 175ms ease-in-out;
  transition-property: opacity, color;

  &:hover {
    opacity: 1 !important;
  }
`;

const List = styled.ul`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 16px;

  &:hover ${Item} {
    opacity: 0.5;
  }
`;

const Underline = styled(animated.li)`
  width: 100px;
  height: 2px;
  background: #20446a;
  position: absolute;
  bottom: -8px;
  left: 0;
`;

const Cta = styled.div<{ sticky: boolean }>`
  display: ${(props) => (props.sticky ? 'block' : 'none')};
  margin-left: auto;

  > a {
    font-size: 16px;
  }
`;

const SliderList = ({ children }: any) => {
  const ref = useRef<HTMLUListElement>(null);
  const [underline, spring] = useSpring(() => ({
    x: 0,
    width: 0,
  }));

  const reset = ({ immediate = false }: { immediate: boolean }) => {
    if (ref.current) {
      const host = ref.current;
      const activeIndex = Children.toArray(children).findIndex((node: any) => {
        return node.props.active;
      });
      const item = host.children[activeIndex];
      const hostRect = host.getBoundingClientRect();
      if (item) {
        const itemRect = item.getBoundingClientRect();
        spring.start({
          x: itemRect.x - hostRect.x + ITEM_GUTTER,
          width: itemRect.width - ITEM_GUTTER * 2,
          immediate,
        });
      } else {
        spring.start({ width: 0 });
      }
    }
  };

  const onMouseOver = React.useCallback(
    (e: React.MouseEvent<HTMLLIElement>) => {
      if (ref.current) {
        const item = e.currentTarget.getBoundingClientRect();
        const host = ref.current.getBoundingClientRect();
        spring.start({
          x: item.x - host.x + ITEM_GUTTER,
          width: item.width - ITEM_GUTTER * 2,
        });
      }
    },
    [spring],
  );

  const onMouseOut = () => {
    reset({ immediate: false });
  };

  useEffect(() => {
    setTimeout(() => {
      reset({ immediate: true });
    }, 10);
  }, []);

  return (
    <List ref={ref}>
      {Children.map(children, (child) => {
        return cloneElement(child, {
          onMouseOver,
          onMouseOut,
          onFocus: onMouseOver,
          onBlur: onMouseOut,
        });
      })}
      <Underline style={underline} />
    </List>
  );
};

const Title = styled.h2<{ sticky: number }>`
  font-weight: bold;
  line-height: 1.2;
  font-size: 16px;
  margin-bottom: -2px;

  a {
    margin-right: 14px;
  }
`;

export function Subnav({
  title,
  pageTitle,
  children,
  active,
  cta,
  mobilecta,
  mobileItems,
  isTransparent,
  titleWidth,
  className,
}: SubnavProps) {
  const { isSubnav, setIsSubnav } = useContext(LayoutContext);
  const [sticky, setSticky] = useState(0);
  const { y, shadow } = useSpring({
    y: sticky === 2 ? 0 : -80,
    shadow: sticky === 2 ? 1 : 0,
  });

  useEffect(() => {
    const heroEl = document.querySelector<HTMLDivElement>('#hero');
    const boundingRect = heroEl
      ? heroEl.getBoundingClientRect()
      : { top: 120, height: 80 };

    const onScroll = () => {
      if (!boundingRect) {
        return;
      }
      if (window.scrollY <= boundingRect.top) {
        setSticky(0);
      } else if (window.scrollY > boundingRect.top + boundingRect.height) {
        setSticky(2);
      } else {
        setSticky(1);
      }
    };
    setIsSubnav(() => true);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      setIsSubnav(() => false);
      window.removeEventListener('scroll', onScroll);
    };
  }, [isSubnav, setIsSubnav]);

  const hasItems = Children.toArray(children).length > 0;

  return (
    <>
      <MobileSubnav
        title={pageTitle}
        items={mobileItems}
        cta={mobilecta}
        hasItems={hasItems}
      />
      <Host isTransparent={isTransparent} className={className}>
        <Sticky
          style={{
            position: sticky === 0 ? 'relative' : 'fixed',
            y: sticky > 0 ? y : undefined,
            boxShadow: shadow.to((s) => {
              return `0 2px 4px rgba(0, 0, 0, ${s * 0.075})`;
            }),
          }}
          isTransparent={isTransparent && sticky === 0}
        >
          <Container>
            <Title sticky={sticky} style={{ width: titleWidth }}>
              {title}
            </Title>
            <SliderList>{children}</SliderList>
            {cta && <Cta sticky={sticky !== 0}>{cta}</Cta>}
          </Container>
        </Sticky>
      </Host>
    </>
  );
}

Subnav.Host = Host;
